/**
 * 24절기 계산.
 *
 * 주 데이터: Swiss Ephemeris (JPL DE431) 기반 생성 데이터 (1900~2100년).
 * 폴백: Jean Meeus 천문 알고리즘 + Newton-Raphson 반복법
 * (생성 데이터 범위 외의 연도에 사용).
 */

import { dateToJD, jdToDate, jdToGregorian } from './julian-day';
import { solarLongitude } from './solar-longitude';
import type { SolarTermEntry } from '../types/input';
import { ALL_SOLAR_TERMS } from '../core/constants';
import {
  GENERATED_SOLAR_TERMS,
  GENERATED_START_YEAR,
  GENERATED_END_YEAR,
} from './generated-solar-terms';

// Memoization cache for solar terms by year
const solarTermsCache = new Map<number, SolarTermEntry[]>();

/**
 * 태양이 목표 황경에 도달하는 율리우스일을 구한다.
 *
 * Newton-Raphson 반복법 사용. 수렴 기준 < 0.000001도 (시간 오차 < 1초).
 *
 * @param targetLongitude - 목표 태양황경 (도, [0, 360))
 * @param startJD - 율리우스일 초기 추정값
 * @returns 태양이 목표 황경에 도달하는 율리우스일
 */
export function findSolarLongitudeTime(targetLongitude: number, startJD: number): number {
  let jd = startJD;

  for (let i = 0; i < 50; i++) {
    const currentLon = solarLongitude(jd);
    let diff = targetLongitude - currentLon;

    // Handle wrap-around at 0/360 boundary
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;

    // Convergence check: < 0.000001 degrees (approximately < 1 second of time)
    if (Math.abs(diff) < 0.000001) {
      return jd;
    }

    // Coarse step using mean solar motion: ~0.9856 degrees per day
    jd += diff / 0.9856;

    // Refine with numerical derivative near convergence
    if (Math.abs(diff) < 1) {
      const delta = 0.001;
      const lon1 = solarLongitude(jd - delta);
      const lon2 = solarLongitude(jd + delta);
      let rate = lon2 - lon1;
      if (rate < -180) rate += 360;
      if (rate > 180) rate -= 360;
      rate /= 2 * delta;

      if (Math.abs(rate) > 0.1) {
        const currentLon2 = solarLongitude(jd);
        let diff2 = targetLongitude - currentLon2;
        if (diff2 > 180) diff2 -= 360;
        if (diff2 < -180) diff2 += 360;
        jd += diff2 / rate;
      }
    }
  }

  return jd; // Should have converged well before 50 iterations
}

/**
 * 지정 연도에서 태양이 특정 황경에 도달하는 시점의 초기 추정값을 계산한다.
 *
 * 춘분점 기준 평균 태양 운동에 기반한다.
 *
 * @param year - 양력 연도
 * @param targetLongitude - 목표 태양황경 (도, [0, 360))
 * @returns 추정 율리우스일
 */
function estimateSolarTermJD(year: number, targetLongitude: number): number {
  // Spring equinox (longitude 0 deg) is approximately March 20 at noon
  const equinoxJD = dateToJD(year, 3, 20, 12, 0, 0);

  // Days from equinox to target longitude based on mean solar motion
  // Mean sun traverses 360 degrees in ~365.25 days
  let lonDiff = targetLongitude;
  if (lonDiff < 0) lonDiff += 360;

  const daysFromEquinox = lonDiff * 365.25 / 360;

  return equinoxJD + daysFromEquinox;
}

/**
 * Swiss Ephemeris 생성 데이터로 해당 연도의 SolarTermEntry[]를 구성한다.
 *
 * @param year - 양력 연도 (생성 데이터 범위 내여야 함)
 * @returns SolarTermEntry 배열, 데이터 범위 밖이면 null
 */
function buildFromGenerated(year: number): SolarTermEntry[] | null {
  const jds = GENERATED_SOLAR_TERMS[year];
  if (!jds) return null;

  const entries: SolarTermEntry[] = [];

  for (let i = 0; i < ALL_SOLAR_TERMS.length; i++) {
    const [name, longitude] = ALL_SOLAR_TERMS[i];
    const jd = jds[i];

    entries.push({
      name,
      longitude,
      jd,
      date: jdToDate(jd),
    });
  }

  // Sort by Julian Day (chronological order)
  entries.sort((a, b) => a.jd - b.jd);
  return entries;
}

/**
 * Calculate all 24 solar terms for a given year using the Jean Meeus algorithm.
 * Used as fallback when KASI data is not available.
 *
 * @param year - Gregorian year
 * @returns Array of SolarTermEntry for all 24 solar terms
 */
function calculateSolarTerms(year: number): SolarTermEntry[] {
  const entries: SolarTermEntry[] = [];

  for (const [name, longitude] of ALL_SOLAR_TERMS) {
    const estimatedJD = estimateSolarTermJD(year, longitude);
    const jd = findSolarLongitudeTime(longitude, estimatedJD);

    // Verify the result is in the expected year
    const greg = jdToGregorian(jd);

    if (greg.year !== year) {
      // For terms near year boundaries (e.g., 소한 at 285 deg occurs in early January),
      // the estimate might land in the wrong year. Retry with an adjusted estimate.
      const adjustedEstimate = estimatedJD + (year - greg.year) * 365.25;
      const retryJD = findSolarLongitudeTime(longitude, adjustedEstimate);
      const retryGreg = jdToGregorian(retryJD);

      if (retryGreg.year === year) {
        entries.push({
          name,
          longitude,
          jd: retryJD,
          date: jdToDate(retryJD),
        });
      }
      continue;
    }

    entries.push({
      name,
      longitude,
      jd,
      date: jdToDate(jd),
    });
  }

  // Sort by Julian Day (chronological order)
  entries.sort((a, b) => a.jd - b.jd);
  return entries;
}

/**
 * Calculate all 24 solar terms for a given year.
 * Returns entries sorted by Julian Day (chronological order).
 * Results are memoized by year.
 *
 * For years within generated data coverage (1900~2100), uses Swiss Ephemeris
 * (JPL DE431) pre-computed data. For years outside that range, falls back to
 * Jean Meeus astronomical algorithm.
 *
 * @param year - Gregorian year
 * @returns Array of SolarTermEntry for all 24 solar terms
 */
export function getSolarTermsForYear(year: number): SolarTermEntry[] {
  const cached = solarTermsCache.get(year);
  if (cached) return cached;

  let entries: SolarTermEntry[];

  // Use Swiss Ephemeris generated data if available (1900~2100)
  if (year >= GENERATED_START_YEAR && year <= GENERATED_END_YEAR) {
    const generatedEntries = buildFromGenerated(year);
    entries = generatedEntries ?? calculateSolarTerms(year);
  } else {
    entries = calculateSolarTerms(year);
  }

  solarTermsCache.set(year, entries);
  return entries;
}

/**
 * Jeol-Gi (절/節) solar term longitudes, names, and corresponding earthly branch indices.
 *
 * A Jeol (절/節) is a "sectional" solar term that begins each lunar month:
 * - 입춘(315 deg) -> 寅 month 1
 * - 경칩(345 deg) -> 卯 month 2
 * - 청명(15 deg)  -> 辰 month 3
 * - 입하(45 deg)  -> 巳 month 4
 * - 망종(75 deg)  -> 午 month 5
 * - 소서(105 deg) -> 未 month 6
 * - 입추(135 deg) -> 申 month 7
 * - 백로(165 deg) -> 酉 month 8
 * - 한로(195 deg) -> 戌 month 9
 * - 입동(225 deg) -> 亥 month 10
 * - 대설(255 deg) -> 子 month 11
 * - 소한(285 deg) -> 丑 month 12
 */
const JEOL_GI_LONGITUDES: readonly [string, number, number][] = [
  ['입춘', 315, 2],
  ['경칩', 345, 3],
  ['청명', 15, 4],
  ['입하', 45, 5],
  ['망종', 75, 6],
  ['소서', 105, 7],
  ['입추', 135, 8],
  ['백로', 165, 9],
  ['한로', 195, 10],
  ['입동', 225, 11],
  ['대설', 255, 0],
  ['소한', 285, 1],
];

/** Set of Jeol (절) term names for quick lookup */
const JEOL_NAMES = new Set(JEOL_GI_LONGITUDES.map(j => j[0]));

/**
 * Find the Jeol-Gi (절/節) solar term that defines the month boundary for a given date.
 * Returns the most recent Jeol before or at the given Julian Day.
 *
 * @param jd - Julian Day Number
 * @param year - Gregorian year (used to fetch solar terms)
 * @returns The most recent Jeol term, or null if not found
 */
export function findCurrentJeolgi(
  jd: number,
  year: number,
): { name: string; longitude: number; branchIndex: number; jd: number } | null {
  // Get solar terms for current year and previous year to handle year boundaries
  const termsThisYear = getSolarTermsForYear(year);
  const termsPrevYear = getSolarTermsForYear(year - 1);
  const allTerms = [...termsPrevYear, ...termsThisYear];

  // Filter to only Jeol (절) terms that occur before or at the given JD
  const jeolTerms = allTerms
    .filter(t => JEOL_NAMES.has(t.name))
    .filter(t => t.jd <= jd)
    .sort((a, b) => b.jd - a.jd); // Most recent first

  if (jeolTerms.length === 0) return null;

  const latest = jeolTerms[0];
  const jeolInfo = JEOL_GI_LONGITUDES.find(j => j[0] === latest.name);
  if (!jeolInfo) return null;

  return {
    name: latest.name,
    longitude: jeolInfo[1],
    branchIndex: jeolInfo[2],
    jd: latest.jd,
  };
}

/**
 * Find the next Jeol-Gi (절/節) solar term after a given Julian Day.
 *
 * @param jd - Julian Day Number
 * @param year - Gregorian year (used to fetch solar terms)
 * @returns The next Jeol term after the given JD, or null if not found
 */
export function findNextJeolgi(
  jd: number,
  year: number,
): { name: string; longitude: number; branchIndex: number; jd: number } | null {
  const termsThisYear = getSolarTermsForYear(year);
  const termsNextYear = getSolarTermsForYear(year + 1);
  const allTerms = [...termsThisYear, ...termsNextYear];

  // Filter to only Jeol (절) terms that occur after the given JD
  const jeolTerms = allTerms
    .filter(t => JEOL_NAMES.has(t.name))
    .filter(t => t.jd > jd)
    .sort((a, b) => a.jd - b.jd); // Earliest first

  if (jeolTerms.length === 0) return null;

  const next = jeolTerms[0];
  const jeolInfo = JEOL_GI_LONGITUDES.find(j => j[0] === next.name);
  if (!jeolInfo) return null;

  return {
    name: next.name,
    longitude: jeolInfo[1],
    branchIndex: jeolInfo[2],
    jd: next.jd,
  };
}

/**
 * Find the previous Jeol-Gi (절/節) solar term before a given Julian Day.
 * This is equivalent to findCurrentJeolgi and is provided as a semantic alias
 * for use in major fortune (대운) calculation.
 *
 * @param jd - Julian Day Number
 * @param year - Gregorian year (used to fetch solar terms)
 * @returns The most recent Jeol term before the given JD, or null if not found
 */
export function findPreviousJeolgi(
  jd: number,
  year: number,
): { name: string; longitude: number; branchIndex: number; jd: number } | null {
  return findCurrentJeolgi(jd, year);
}

